import React, { ChangeEvent, useState } from "react";
import { FieldArray, useFormikContext } from "formik";
import {
  Droppable,
  Draggable,
  DropResult,
  DragDropContext,
} from "react-beautiful-dnd";
import {
  FormSection as FormSectionType,
  FormField as FormFieldType,
  FormFieldType as FormFieldTypeType,
} from "@slootsantos/certain-forms/dist/types";
import { Trans, useTranslation } from "react-i18next";

import FormField from "forms/FormField";
import FormSection from "components/form/FormSection";
import { DocumentUpload } from "components/DocumentUpload/Uploader";
import { calculateScore } from "components/Risk/utils";
import { FieldCondition } from "forms/types";

const evaluateCondition = (
  condition: FieldCondition,
  sectionValues: Record<string, unknown>,
  allValues: Record<string, unknown>,
  allFields: Array<{ name: string; condition?: FieldCondition }> = [],
  visited: Set<string> = new Set(),
): boolean => {
  // For same-section dependencies, check if the depended-upon field is itself visible
  if (!condition.field.includes(".")) {
    if (visited.has(condition.field)) return true; // break circular dependency
    visited.add(condition.field);

    const depField = allFields.find((f) => f.name === condition.field);
    if (depField?.condition) {
      const depVisible = evaluateCondition(
        depField.condition,
        sectionValues,
        allValues,
        allFields,
        visited,
      );
      if (!depVisible) return false;
    }
  }

  let watched: unknown;
  if (condition.field.includes(".")) {
    const [sec, f] = condition.field.split(".");
    watched = (allValues[sec] as any)?.[f];
  } else {
    watched = sectionValues?.[condition.field];
  }
  switch (condition.operator) {
    case "eq":
      return watched === condition.value;
    case "neq":
      return watched !== condition.value;
    case "includes":
      return Array.isArray(watched)
        ? watched.includes(condition.value)
        : String(watched).includes(String(condition.value));
    case "truthy":
      return watched !== "0" && Boolean(watched);
    case "not_truthy":
      return watched === "0" || !Boolean(watched);
    case "oneof":
      return (
        Array.isArray(condition.value) && condition.value.includes(watched)
      );
  }
};

// Configuration for collapsible items summary
interface CollapsedItemSummaryConfig {
  // Fields to show in summary when collapsed
  summaryFields?: string[];
  // Custom render function for collapsed state
  renderSummary?: (item: any, index: number) => React.ReactNode;
}

interface FormBodyProps {
  context?: string;
  withDivider?: boolean;
  section: FormSectionType;
  confirmationLabel?: string;
  confirmationSubline?: string;
  onAdd?: (values: any) => Record<string, string>;
  onChange: (
    fieldName: string,
    transform: FormFieldType["transform"],
  ) => (e: React.ChangeEvent<any>) => void;
  // Configuration for collapsed item summaries
  collapsedSummaryConfig?: CollapsedItemSummaryConfig;
}

export const FormBody = (props: FormBodyProps) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();
  const { section, withDivider = false, onChange } = props;

  const fieldDefs = section.fields.map((f) => ({
    name: f.name,
    condition: (f as any).condition as FieldCondition | undefined,
  }));

  // State to track which items are collapsed
  const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>(
    () => {
      // Get the current items
      const sectionValues = (values as any)[section.name];
      const items = Array.isArray(sectionValues) ? sectionValues : [];

      // Create a map of collapsed states
      const collapsedMap: Record<number, boolean> = {};

      // If there's only one item, don't collapse it by default
      // If there are multiple items, collapse them all by default
      items.forEach((_, index) => {
        collapsedMap[index] = items.length > 1; // true means collapsed
      });

      return collapsedMap;
    },
  );

  // Toggle collapse/expand for a specific item
  const toggleCollapse = (index: number) => {
    setCollapsedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (props.section.isGroup) {
    return (
      <>
        {props.section.groups?.map((group, idx) => {
          const isFirst = idx === 0;
          const sectionValues = (values as any)[section.name] ?? {};

          const visibleFields = section.fields
            .filter((field) => field.groupName === group.name)
            .filter((field) => {
              const condition = (field as any).condition as
                | FieldCondition
                | undefined;
              if (!condition) return true;
              return evaluateCondition(
                condition,
                sectionValues,
                values as any,
                fieldDefs,
              );
            });

          if (visibleFields.length === 0) return null;

          return (
            <FormSection
              key={group.name + idx}
              withDivider={withDivider}
              sectionName={isFirst ? section.name : ""}
              title={isFirst ? section.label : ""}
              infoText={isFirst ? section.info : ""}
              subline={isFirst ? section.subline : ""}
              confirmationLabel={props.confirmationLabel}
              confirmationSubline={props.confirmationSubline}
            >
              <h3 className="text-lg col-span-6">{t(group.label || "")}</h3>
              {visibleFields.map((field, idx) => {
                const fieldName = `${section.name}.${field.name}`;

                if (field.type === FormFieldTypeType.informational) {
                  return field.customRenderOverride!({
                    ...(values as object),
                    idx: 0,
                  });
                }

                if (field.type === FormFieldTypeType.upload) {
                  return (
                    <DocumentUpload
                      onChange={(f: File | null) => {
                        onChange(
                          "_uploads." + field.name,
                          undefined,
                        )({
                          target: {
                            value: { image: f, key: f?.name, touched: true },
                          },
                        } as ChangeEvent<any>);
                      }}
                      file={
                        (values as any)?._uploads &&
                        (values as any)?._uploads[field.name]?.image
                      }
                      location={
                        (values as any)?._uploads &&
                        (values as any)?._uploads[field.name]?.location
                      }
                      type={"type"}
                      key={field.name}
                      name={field.name}
                      subline={field.info!}
                      label={field.label}
                      enableCompression={field.name === "manufacturer_logo"}
                    />
                  );
                }

                return (
                  <FormField
                    {...field}
                    data-test={fieldName}
                    key={fieldName + idx}
                    name={fieldName}
                    onBlur={onChange(fieldName, field.transform)}
                    defaultValue={
                      values &&
                      (values as any)[section.name] &&
                      (values as any)[section.name][field.name]
                    }
                  />
                );
              })}
            </FormSection>
          );
        })}
      </>
    );
  }

  if (props.section.isList) {
    return (
      <FieldArray
        name={section.name}
        render={(arrayHelpers) => {
          // Handle drag end event - reorder the items in the form values
          const handleDragEnd = (result: DropResult) => {
            // Dropped outside the list
            if (!result.destination) {
              return;
            }

            const sourceIndex = result.source.index;
            const destinationIndex = result.destination.index;

            // Don't do anything if the position didn't change
            if (sourceIndex === destinationIndex) {
              return;
            }

            // Get current form values for this section
            const currentItems = [...((values as any)[section.name] || [])];

            // Remove the item from its original position
            const [removed] = currentItems.splice(sourceIndex, 1);

            // Insert the item at the new position
            currentItems.splice(destinationIndex, 0, removed);

            // Update the form values with the new order
            arrayHelpers.form.setFieldValue(section.name, currentItems);

            if (section.name === "mitigations") {
              window.alert(t("form.mitigations.resorted_alert"));
            }
          };

          // List items - these will be draggable
          const listItems = (values as any)[section.name]
            ? (values as any)[section.name].map((item: any, index: number) => {
                const isFirst = index === 0;

                return (
                  <Draggable
                    key={`${section.name}-item-${index}`}
                    draggableId={`${section.name}-item-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.8 : 1,
                        }}
                      >
                        <div className="flex items-center my-2 w-full">
                          <div
                            {...provided.dragHandleProps}
                            className="p-1 mr-2 cursor-grab flex-shrink-0  flex items-center"
                            title="Drag to reorder"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6 mr-2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                              />
                            </svg>
                          </div>
                          <FormSection
                            key={section.name + index}
                            withDivider={false}
                            title={isFirst ? "" : ""}
                            infoText={""}
                            subline={""}
                            sectionName={""}
                            withDeletion
                            hideLeftColumn={true}
                            deletionHandler={() => arrayHelpers.remove(index)}
                            confirmationLabel={props.confirmationLabel}
                            confirmationSubline={props.confirmationSubline}
                          >
                            <div className="col-span-6 flex flex-col">
                              <div className="flex items-center mb-2">
                                <div
                                  className="flex items-center cursor-pointer flex-grow"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    toggleCollapse(index);
                                  }}
                                  title={
                                    collapsedItems[index]
                                      ? "Expand"
                                      : "Collapse"
                                  }
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    className={`ml-2 mr-1 transition-transform ${
                                      collapsedItems[index]
                                        ? ""
                                        : "transform rotate-180"
                                    }`}
                                  >
                                    {/* ChevronDown Icon - it will rotate when expanded */}
                                    <path
                                      fillRule="evenodd"
                                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  <h4 className="text-lg font-medium">
                                    {t(`counter.lists.${section.label}`)} #
                                    {index + 1}
                                  </h4>
                                  <span className="text-xs text-gray-500 ml-2">
                                    - {t("common.click_to_edit")}
                                  </span>
                                </div>
                              </div>

                              {/* Summary content when collapsed */}
                              {collapsedItems[index] && (
                                <div
                                  className="ml-8 mb-2 text-sm text-gray-700  cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    toggleCollapse(index);
                                  }}
                                >
                                  {props.collapsedSummaryConfig
                                    ?.renderSummary ? (
                                    // If a custom render function is provided, use it
                                    props.collapsedSummaryConfig.renderSummary(
                                      item,
                                      index,
                                    )
                                  ) : props.collapsedSummaryConfig
                                      ?.summaryFields ? (
                                    // If summary fields are provided, render them
                                    <div className="grid grid-cols-2 gap-2">
                                      {props.collapsedSummaryConfig.summaryFields.map(
                                        (fieldName) => {
                                          const field = section.fields.find(
                                            (f) => f.name === fieldName,
                                          );
                                          if (!field) return null;

                                          const value = item && item[fieldName];
                                          return (
                                            <div
                                              key={fieldName}
                                              className="flex flex-row"
                                            >
                                              <span className="font-medium mr-2">
                                                {t(field.label)}:
                                              </span>
                                              <span className="truncate">
                                                {value || "-"}
                                              </span>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  ) : (
                                    // Intelligent default behavior based on section type
                                    <div className="py-1">
                                      {(() => {
                                        // Determine section type based on name pattern or fields structure
                                        if (
                                          section.name === "mitigations" ||
                                          section.name.includes("mitigation")
                                        ) {
                                          // For risk mitigations, show description field
                                          const description =
                                            item?.description ||
                                            item?.mitigation_description;
                                          const type = item?.type;
                                          return (
                                            <>
                                              {type && (
                                                <div className="text-sm">
                                                  <div>
                                                    <span className="text-md font-medium text-gray-500">
                                                      {t(
                                                        "form.mitigations.type",
                                                      )}
                                                      :
                                                    </span>{" "}
                                                    {t(
                                                      `form.mitigations.options.type.${type}`,
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                              <div>
                                                <span className="text-md font-medium text-gray-500 pt-2">
                                                  {t(
                                                    "form.risk.remaining_risk",
                                                  )}
                                                </span>
                                                {calculateScore(item)}
                                              </div>
                                              <div className="overflow-hidden text-ellipsis max-w-full whitespace-nowrap">
                                                <span className="text-md font-medium text-gray-500">
                                                  {t(
                                                    "form.mitigations.description",
                                                  )}
                                                  :{" "}
                                                </span>
                                                {description && (
                                                  <span className="text-sm">
                                                    {description}
                                                  </span>
                                                )}
                                                {!description && (
                                                  <span className="italic text-gray-500">
                                                    No description available
                                                  </span>
                                                )}
                                              </div>
                                            </>
                                          );
                                        } else if (
                                          section.name === "team" ||
                                          section.name.includes("team")
                                        ) {
                                          // For team members, show name and area/role
                                          const name = item?.name;
                                          const area = item?.area;
                                          return (
                                            <>
                                              {name && (
                                                <div className="font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                                  {name}
                                                </div>
                                              )}
                                              {area && (
                                                <div className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                                  {area}
                                                </div>
                                              )}
                                            </>
                                          );
                                        } else if (
                                          section.name ===
                                            "boundaryAdditional" ||
                                          section.name.includes("boundary")
                                        ) {
                                          // For boundaries, show description and value
                                          const description = item?.description;
                                          const value = item?.value;
                                          return (
                                            <>
                                              {description && (
                                                <div className="font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                                  {description}
                                                </div>
                                              )}
                                              {value && (
                                                <div className="text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                                  {value}
                                                </div>
                                              )}
                                            </>
                                          );
                                        } else {
                                          // Try to find the most important field to display as a fallback
                                          // Common field names that typically contain key information
                                          const importantFields = [
                                            "name",
                                            "title",
                                            "label",
                                            "description",
                                            "value",
                                          ];

                                          // Find first existing important field
                                          let displayValue = null;
                                          for (const fieldName of importantFields) {
                                            if (item && item[fieldName]) {
                                              displayValue = item[fieldName];
                                              break;
                                            }
                                          }

                                          return displayValue ? (
                                            <div className="text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                              {displayValue}
                                            </div>
                                          ) : (
                                            <div className="italic text-gray-500">
                                              Click to expand
                                            </div>
                                          );
                                        }
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Fields - only shown when not collapsed */}
                            {!collapsedItems[index] &&
                              section.fields.map((field) => {
                                if (
                                  field.type === FormFieldTypeType.informational
                                ) {
                                  return field.customRenderOverride!({
                                    ...(values as object),
                                    idx: index,
                                  });
                                }

                                const fieldName = `${section.name}[${index}].${field.name}`;
                                const fieldValue =
                                  values &&
                                  (values as any)[section.name] &&
                                  (values as any)[section.name][index] &&
                                  (values as any)[section.name][index][
                                    field.name
                                  ];
                                return (
                                  <FormField
                                    {...field}
                                    data-test={fieldName}
                                    uncontrolled
                                    name={fieldName}
                                    defaultValue={fieldValue}
                                    key={fieldName + index + fieldValue}
                                    onBlur={onChange(
                                      fieldName,
                                      field.transform,
                                    )}
                                  />
                                );
                              })}
                          </FormSection>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })
            : [];

          // Render the section information once, outside of the draggable items
          return (
            <div>
              {/* Section information - rendered only once */}
              <div className="mb-6">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                  <div className="md:col-span-1">
                    <div className="px-4 sm:px-0">
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {t(section.label)}
                      </h3>
                      <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">
                        {t(section.subline)}
                      </p>
                      <p className="mt-6 text-sm text-gray-600 whitespace-pre-line h-0">
                        <Trans
                          i18nKey={
                            section.info || `form.${section.name}.info` || ""
                          }
                        />
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 md:mt-0 md:col-span-2">
                    {/* Draggable content goes here */}
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId={`${section.name}-list`}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="p-2 bg-gray-100 rounded-md"
                          >
                            {listItems}
                            {provided.placeholder}

                            <div className="text-right py-3 rounded-md mt-2">
                              <button
                                data-test="add-list-item-button"
                                type="button"
                                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                                onClick={() => {
                                  // Get current length before adding new item
                                  const currentLength = (
                                    (values as any)[section.name] || []
                                  ).length;

                                  // Add the new item
                                  props.onAdd
                                    ? arrayHelpers.push(
                                        props.onAdd(values) || {},
                                      )
                                    : arrayHelpers.push({});

                                  // Set the newly added item to be expanded by default
                                  setCollapsedItems((prev) => {
                                    // If this is the second item being added, also collapse the first item
                                    if (currentLength === 1) {
                                      return {
                                        ...prev,
                                        0: true, // Collapse the first item
                                        [currentLength]: false, // Expand the new item
                                      };
                                    }

                                    // Otherwise just expand the new item
                                    return {
                                      ...prev,
                                      [currentLength]: false, // false means expanded
                                    };
                                  });
                                }}
                              >
                                {t("counter.lists.add", {
                                  entity: t(`counter.lists.${section.label}`),
                                })}
                              </button>
                            </div>
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />
    );
  }

  return (
    <FormSection
      key={section.name}
      sectionName={section.name}
      title={section.label}
      subline={section.subline}
      withDivider={withDivider}
      infoText={section.info}
      confirmationLabel={props.confirmationLabel}
      confirmationSubline={props.confirmationSubline}
    >
      {section.fields.map((field) => {
        const condition = (field as any).condition as
          | FieldCondition
          | undefined;
        if (condition) {
          const sectionValues = (values as any)[section.name] ?? {};
          if (
            !evaluateCondition(
              condition,
              sectionValues,
              values as any,
              fieldDefs,
            )
          )
            return null;
        }

        const fieldName = `${section.name}.${field.name}`;

        if (field.type === FormFieldTypeType.informational) {
          return field.customRenderOverride!({
            ...(values as object),
            idx: 0,
          });
        }

        if (field.type === FormFieldTypeType.upload) {
          return (
            <DocumentUpload
              onChange={(f: File | null) => {
                onChange(
                  "_uploads." + field.name,
                  undefined,
                )({
                  target: {
                    value: { image: f, key: f?.name, touched: true },
                  },
                } as ChangeEvent<any>);
              }}
              file={
                (values as any)?._uploads &&
                (values as any)?._uploads[field.name]?.image
              }
              location={
                (values as any)?._uploads &&
                (values as any)?._uploads[field.name]?.location
              }
              type={"type"}
              key={field.name}
              name={field.name}
              subline={field.info!}
              label={field.label}
            />
          );
        }

        return (
          <FormField
            {...field}
            data-test={fieldName}
            key={props.context + fieldName}
            name={fieldName}
            onBlur={onChange(fieldName, field.transform)}
            defaultValue={
              values &&
              (values as any)[section.name] &&
              (values as any)[section.name][field.name]
            }
          />
        );
      })}
    </FormSection>
  );
};
